import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import { apiClient } from '@/utils/api-client';
import {
  People,
  Home,
  Analytics,
  TrendingUp,
  Refresh,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
} from '@mui/icons-material';

interface SystemMetrics {
  totalHosts: number;
  totalGuests: number;
  totalListings: number;
  totalBookings: number;
  activeListings: number;
  pendingModerations: number;
  systemHealth: 'healthy' | 'warning' | 'critical';
  lastUpdated: string;
}

interface SystemMetricsCardsProps {
  autoRefresh?: boolean;
  refreshInterval?: number; // in milliseconds
  onRefresh?: () => void;
}

export const SystemMetricsCards: React.FC<SystemMetricsCardsProps> = ({
  autoRefresh = true,
  refreshInterval = 30000, // 30 seconds
  onRefresh,
}) => {
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Make actual API call to get system metrics using API client
      const response = await apiClient.get<SystemMetrics>('/admin/dashboard/metrics');
      setMetrics(response.data);
      setLastRefresh(new Date());
      
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      setError('Failed to fetch system metrics');
      console.error('Error fetching system metrics:', err);
    } finally {
      setLoading(false);
    }
  }, [onRefresh]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  useEffect(() => {
    if (autoRefresh && refreshInterval > 0) {
      const interval = setInterval(fetchMetrics, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval, fetchMetrics]);

  const handleManualRefresh = () => {
    fetchMetrics();
  };

  const getHealthIcon = (health: string) => {
    switch (health) {
      case 'healthy':
        return <CheckCircle sx={{ color: 'success.main' }} />;
      case 'warning':
        return <Warning sx={{ color: 'warning.main' }} />;
      case 'critical':
        return <ErrorIcon sx={{ color: 'error.main' }} />;
      default:
        return <CheckCircle sx={{ color: 'success.main' }} />;
    }
  };

  const getHealthColor = (health: string) => {
    switch (health) {
      case 'healthy':
        return 'success';
      case 'warning':
        return 'warning';
      case 'critical':
        return 'error';
      default:
        return 'success';
    }
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
        <IconButton size="small" onClick={handleManualRefresh} sx={{ ml: 1 }}>
          <Refresh />
        </IconButton>
      </Alert>
    );
  }

  if (loading && !metrics) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!metrics) {
    return null;
  }

  const metricsCards = [
    {
      title: 'Total Hosts',
      value: metrics.totalHosts,
      icon: <People sx={{ color: 'primary.main' }} />,
      color: 'primary.main',
      subtitle: 'Registered hosts',
    },
    {
      title: 'Total Guests',
      value: metrics.totalGuests,
      icon: <People sx={{ color: 'secondary.main' }} />,
      color: 'secondary.main',
      subtitle: 'Registered guests',
    },
    {
      title: 'Active Listings',
      value: metrics.activeListings,
      icon: <Home sx={{ color: 'success.main' }} />,
      color: 'success.main',
      subtitle: `of ${metrics.totalListings} total`,
    },
    {
      title: 'Total Bookings',
      value: metrics.totalBookings,
      icon: <Analytics sx={{ color: 'info.main' }} />,
      color: 'info.main',
      subtitle: 'All-time bookings',
    },
  ];

  return (
    <Box>
      {/* Header with refresh controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
          System Metrics
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </Typography>
          <Tooltip title="Refresh metrics">
            <IconButton size="small" onClick={handleManualRefresh} disabled={loading}>
              <Refresh sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {metricsCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.title}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {card.icon}
                  <Typography variant="h6" component="div" sx={{ ml: 1 }}>
                    {card.title}
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: card.color, mb: 0.5 }}>
                  {card.value.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {card.subtitle}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* System Health Indicators */}
      <Card>
        <CardContent>
          <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 'bold' }}>
            System Health & Status
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {getHealthIcon(metrics.systemHealth)}
                  <Typography variant="body1" sx={{ ml: 1 }}>
                    System Health
                  </Typography>
                </Box>
                <Chip 
                  label={metrics.systemHealth.charAt(0).toUpperCase() + metrics.systemHealth.slice(1)} 
                  color={getHealthColor(metrics.systemHealth) as any}
                  size="small" 
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TrendingUp sx={{ color: metrics.pendingModerations > 0 ? 'warning.main' : 'success.main' }} />
                  <Typography variant="body1" sx={{ ml: 1 }}>
                    Pending Moderations
                  </Typography>
                </Box>
                <Chip 
                  label={metrics.pendingModerations} 
                  color={metrics.pendingModerations > 0 ? "warning" : "success"} 
                  size="small" 
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body1">Auto-refresh</Typography>
                <Chip 
                  label={autoRefresh ? "Enabled" : "Disabled"} 
                  color={autoRefresh ? "success" : "default"} 
                  size="small" 
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};